import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@core/services';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { MustMatch } from '../_helpers/must-match.validator';
import { Subscription } from 'rxjs';
import { ApiErrorResponse, ApiSuccessResponse } from '@core/models/auth.model';

export  enum ChangePasswordFormType {
  ChangePassword = 'change_password',
  AcceptInvitation = 'accept_invitation'
}
@Component({
    selector: 'app-change-password-form',
    templateUrl: './change-password-form.component.html',
    styleUrls: ['./change-password-form.component.scss'],
    standalone: false
})
export class ChangePasswordFormComponent implements OnInit, OnDestroy {
  @Input() type!: ChangePasswordFormType;
  @Input() inputToken!: string;
  @Input() btnLabel: string = 'form.reset_password';
  @Input() withLogin: boolean = true;
  submitted = false;
  message: string;
  formStep = 1;
  tokenChecking = true;
  tokenValid  = false;
  token!: string;
  changePasswordForm: FormGroup;
  valueChangeSubscription!: Subscription;

  constructor(private fb: FormBuilder,
              private authService: AuthService,
              private route: ActivatedRoute,
              private toastr: ToastrService) {
    this.changePasswordForm = this.fb.group({
        password: ['', [Validators.required, Validators.minLength(6)]],
        password_confirmation: ['', Validators.required],
      },
      {
        validator: MustMatch('password', 'password_confirmation')
      }
    );
  }

  ngOnInit(): void {
    this.token = this.getToken as string;
    this.checkTokenValidity();
    this.valueChangeSubscription = this.changePasswordForm.valueChanges.subscribe(
      () => this.submitted = false
    );
  }

  submitForm(form: FormGroup): void {
    this.submitted = true;
    if (form.valid) {
      switch (this.type) {
        case ChangePasswordFormType.ChangePassword: this.changePassword(form); break;
        case ChangePasswordFormType.AcceptInvitation: this.acceptInvitation(form); break;
      }
    }
  }

  private checkTokenValidity(): void {
    if (this.type === ChangePasswordFormType.ChangePassword) {
      this.authService.resetPassword({ reset_password_token: this.token } as never)
        .subscribe(
          () => {
            this.tokenValid = true;
            this.tokenChecking = false;
          },
          (err: ApiErrorResponse) =>  {
            const errorMessages = Array.isArray(err.errors) ? err.errors : [];
            this.tokenValid = !errorMessages.includes('Unauthorized');
            this.tokenChecking = false;
          }
        );
    } else if (this.type === ChangePasswordFormType.AcceptInvitation) {
      this.authService.invitationResetPassword({ invitation_token: this.token } as never)
        .subscribe(
          () => {
            this.tokenValid = true;
            this.tokenChecking = false;
          },
          (err: ApiErrorResponse) =>  {
            const errorMessages = Array.isArray(err.errors) ? err.errors : [];
            this.tokenValid = !errorMessages.includes('Invalid token.');
            this.tokenChecking = false;
          }
        );
    }

  }

  private acceptInvitation(form: FormGroup): void {
    this.authService.invitationResetPassword({
      password: form.value.password,
      password_confirmation: form.value.password,
      invitation_token: this.token
    }).subscribe(
      (result: ApiSuccessResponse) => {
        this.toastr.success(result.success);
        this.formStep = 2;
        this.submitted = false;
      },
      (error: ApiErrorResponse) => {
        this.message = JSON.stringify(error);
        this.toastr.error(this.extractErrorMessage(error, 'Failed to accept invitation'));
        this.submitted = false;
      }
    );
  }

  private changePassword(form: FormGroup): void {
    this.authService.resetPassword({
      password: form.value.password,
      password_confirmation: form.value.password,
      reset_password_token: this.token,
    }).subscribe(
      () => {
        this.toastr.success('Successfully changed password');
        this.formStep = 2;
        this.submitted = false;
      },
      (error: ApiErrorResponse) => {
        this.toastr.error(this.extractErrorMessage(error, 'Failed to change password'));
        this.submitted = false;
      }
    );
  }

  // FE-327: `error.errors` is a flat string[] for some endpoints (e.g. invalid
  // token) but, on validation failures from /user/auth/password and
  // /user/auth/invitation, Devise returns it keyed by field instead
  // (`{ password: [...], full_messages: [...] }`). `String(error.errors)` on
  // that object renders the literal text "[object Object]" in the toast, so
  // the real message (full_messages, already human-readable) must be read out.
  private extractErrorMessage(error: ApiErrorResponse, fallback: string): string {
    const errors = error?.errors;
    if (Array.isArray(errors)) {
      return errors.join(', ');
    }
    if (typeof errors === 'string') {
      return errors;
    }
    if (errors && Array.isArray(errors.full_messages)) {
      return errors.full_messages.join(', ');
    }
    return fallback;
  }

  get f(): any { return this.changePasswordForm.controls; }

  get getToken(): string | null {
    return this.inputToken || this.route.snapshot.paramMap.get('token');
  }

  ngOnDestroy(): void {
    if (this.valueChangeSubscription) {
      this.valueChangeSubscription.unsubscribe();
    }
  }
}
